import { ChartElement } from '@modules/reports/models/chart-element';
import { ReferralReportsItem } from '@modules/reports/models/referral-reports-item';
import {
  referralReportsPageLoad,
  referralReportsPageLoadSuccess,
  referralReportsPageLoadFail,
  loadJobFunctionsSuccess,
  loadJobDepartmentsSuccess,
  loadReferralsByJobDepartmentSuccess,
  loadReferralsByReferralDepartmentSuccess,
  loadReferralsByJobFunctionSuccess,
  loadReferralsByReferralFunctionSuccess,
  loadAllJobsByParamsSuccess,
  loadAggregatedTotalDirectPerPhasesDataSuccess,
  loadAvgResponseTimeSuccess,
  loadDashboardReportsSuccess,
  loadDashboardReportsSuccessSubmissions,
  loadDashboardReportsSuccessOffer,
  loadDashboardReports,
  loadDashboardReportsFail,
  loadDashboardReportsSubmissions,
  loadDashboardReportsFailSubmissions,
  loadDashboardReportsOffer,
  loadDashboardReportsFailOffer,
  loadOldestReferralDateSuccess,
  loadReferralCountByStageSuccess,
  loadAveragesPerJobSuccess,
  sendEmailSuccessReport,
  sendEmailFailReport,
  changeStateReferralReport,
} from '@modules/reports/store/referral-reports.actions';
import { EntityAdapter, createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AveragesPerJob } from '@shared/models/averages-per-job';
import { AvgResponseTime } from '@shared/models/avg-response-time';
import { Pagination } from '@shared/pagination';
import { DashboardReportModel } from '@shared/models/dashboard-reports';
import { Job } from '@shared';
import { ReferralCountByStage } from '../models/referral-count-by-stage';

export const referralReportsAdapter: EntityAdapter<ReferralReportsItem> =
  createEntityAdapter<ReferralReportsItem>({ selectId: (report) => report.referralId });

export interface ReferralReportsState {
  entities: EntityState<ReferralReportsItem>;
  jobFunctions: string[];
  jobDepartments: string[];
  referralsByDepartment: ChartElement[];
  referralsByJobFunction: ChartElement[];
  referralOldestDate: Date;
  reffalCountByStage: ReferralCountByStage[];
  referralReportsItems: Pagination<ReferralReportsItem>;
  loaded: boolean;
  loading: boolean;
  allJobs: Pagination<Job>;
  allActiveJobsTotalDirectPerPhaseList: any[];
  averageResponseTime: AvgResponseTime[];
  reportsDashboard: DashboardReportModel;
  reportsDashboardSubmission: DashboardReportModel;
  reportsDashboardOffer: DashboardReportModel;
  pipelineAllActiveJobs: Pagination<Job>;
  pipelineAllActiveJobsTotalDirectPerPhaseList: any[];
  dashboardAveragesPerJob: AveragesPerJob;
  referralReportEmailSent: boolean;
}

export const initialState: ReferralReportsState = {
  entities: referralReportsAdapter.getInitialState(),
  jobFunctions: [],
  jobDepartments: [],
  referralsByDepartment: [],
  referralsByJobFunction: [],
  reffalCountByStage: [],
  referralReportsItems: {
    totalElements: 0,
    totalPages: 0,
    size: 10,
    number: 0,
    numberOfElements: 0,
    content: [],
  },
  referralOldestDate: null,
  loaded: false,
  loading: false,
  allJobs: null,
  allActiveJobsTotalDirectPerPhaseList: [],
  averageResponseTime: [],
  reportsDashboard: {
    totalApplicants: 0,
    data: [],
    loadReportStatus: {
      loaded: false,
      loading: false,
      error: false,
    },
  },
  reportsDashboardSubmission: {
    totalApplicants: 0,
    data: [],
    loadReportStatus: {
      loaded: false,
      loading: false,
      error: false,
    },
  },
  reportsDashboardOffer: {
    totalApplicants: 0,
    data: [],
    loadReportStatus: {
      loaded: false,
      loading: false,
      error: false,
    },
  },
  pipelineAllActiveJobs: null,
  pipelineAllActiveJobsTotalDirectPerPhaseList: [],
  dashboardAveragesPerJob: null,
  referralReportEmailSent: false,
};

export const referralReportsReducer = createReducer(
  initialState,
  on(referralReportsPageLoad, (state) => ({ ...state, loading: true })),
  on(referralReportsPageLoadSuccess, (state, { payload }) => ({
    ...state,
    entities: referralReportsAdapter.upsertMany(payload.content, state.entities),
    referralReportsItems: payload,
    loaded: true,
    loading: false,
  })),
  on(referralReportsPageLoadFail, (state) => ({
    ...state,
    loaded: false,
    loading: false,
  })),
  on(loadOldestReferralDateSuccess, (state, { referralOldestDate }) => ({
    ...state,
    referralOldestDate,
  })),
  on(loadJobFunctionsSuccess, (state, { jobFunctions }) => ({
    ...state,
    jobFunctions,
  })),
  on(loadJobDepartmentsSuccess, (state, { jobDepartments }) => ({
    ...state,
    jobDepartments,
  })),
  on(
    loadReferralsByJobDepartmentSuccess,
    loadReferralsByReferralDepartmentSuccess,
    (state, { elements }) => ({
      ...state,
      referralsByDepartment: elements,
    }),
  ),
  on(
    loadReferralsByJobFunctionSuccess,
    loadReferralsByReferralFunctionSuccess,
    (state, { elements }) => ({
      ...state,
      referralsByJobFunction: elements,
    }),
  ),
  on(loadReferralCountByStageSuccess, (state, { counts }) => ({
    ...state,
    reffalCountByStage: counts,
  })),
  on(loadAllJobsByParamsSuccess, (state, { response }) => ({
    ...state,
    allJobs: response,
  })),
  on(
    loadAggregatedTotalDirectPerPhasesDataSuccess,
    (state, { allActiveJobsTotalDirectPerPhaseList: allActiveJobsTotalDirectPerPhaseList }) => ({
      ...state,
      allActiveJobsTotalDirectPerPhaseList,
    }),
  ),
  on(loadAvgResponseTimeSuccess, (state, { response }) => {
    response.forEach((avgTime) => {
      const avgTimeForJobId = state.averageResponseTime.findIndex(
        (res) => res.jobId === avgTime.jobId,
      );
      if (avgTimeForJobId !== -1) {
        state.averageResponseTime[avgTimeForJobId] = avgTime;
      } else {
        state.averageResponseTime.push(avgTime);
      }
    });
    return {
      ...state,
    };
  }),

  on(loadDashboardReports, (state) => {
    state.reportsDashboard.loadReportStatus = {
      loaded: false,
      loading: true,
      error: false,
    };
    return {
      ...state,
    };
  }),
  on(loadDashboardReportsSuccess, (state, { response }) => {
    state.reportsDashboard = response;
    state.reportsDashboard.loadReportStatus = {
      loaded: true,
      loading: false,
      error: false,
    };
    return {
      ...state,
    };
  }),
  on(loadDashboardReportsFail, (state, { error }) => {
    state.reportsDashboard.loadReportStatus = {
      loaded: true,
      loading: false,
      error: true,
    };
    return {
      ...state,
    };
  }),

  on(loadDashboardReportsSubmissions, (state) => {
    state.reportsDashboardSubmission.loadReportStatus = {
      loaded: false,
      loading: true,
      error: false,
    };

    return {
      ...state,
    };
  }),

  on(loadDashboardReportsSuccessSubmissions, (state, { response }) => {
    state.reportsDashboardSubmission = response;
    state.reportsDashboardSubmission.loadReportStatus = {
      loaded: true,
      loading: false,
      error: false,
    };
    return {
      ...state,
    };
  }),
  on(loadDashboardReportsFailSubmissions, (state) => {
    state.reportsDashboardSubmission.loadReportStatus = {
      loaded: true,
      loading: false,
      error: true,
    };

    return {
      ...state,
    };
  }),

  on(loadDashboardReportsOffer, (state) => {
    state.reportsDashboardOffer.loadReportStatus = {
      loaded: false,
      loading: true,
      error: false,
    };
    return {
      ...state,
    };
  }),
  on(loadDashboardReportsSuccessOffer, (state, { response }) => {
    state.reportsDashboardOffer = response;
    state.reportsDashboardOffer.loadReportStatus = {
      loaded: true,
      loading: false,
      error: false,
    };
    return {
      ...state,
    };
  }),
  on(loadDashboardReportsFailOffer, (state) => {
    state.reportsDashboardOffer.loadReportStatus = {
      loaded: true,
      loading: false,
      error: true,
    };
    return {
      ...state,
    };
  }),
  on(loadAveragesPerJobSuccess, (state, { averagesPerJob }) => ({
    ...state,
    dashboardAveragesPerJob: averagesPerJob,
  })),
  on(sendEmailSuccessReport, (state) => ({
    ...state,
    referralReportEmailSent: true,
  })),
  on(sendEmailFailReport, (state) => ({
    ...state,
    referralReportEmailSent: false,
  })),
  on(changeStateReferralReport, (state) => ({
    ...state,
    referralReportEmailSent: false,
  })),
);
